import { Navigate, useLocation } from 'react-router-dom';

import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import useMaintenanceStore from '@/store/useMaintenanceStore';
import useUser from '@/store/useUser';

const MaintenanceGuard = ({ children }: { children: React.ReactNode }) => {
  const { isMaintenanceMode } = useMaintenanceStore();
  const token = useUser((state) => state.user.token);
  const { hasAccess } = useAccess();
  const location = useLocation();

  const isOnMaintenancePage = location.pathname === '/maintenance';
  const canManageMaintenance = hasAccess(ERoleAccess.MAINTENANCE_MODE);

  // Not logged in → skip maintenance guard, let auth routes handle it
  if (!token) {
    return <>{children}</>;
  }

  // Users with maintenance mode access → never redirect to /maintenance; stay on current page
  if (canManageMaintenance) {
    // If they're somehow on /maintenance page with maintenance off, send them home
    if (!isMaintenanceMode && isOnMaintenancePage) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  // Maintenance ON → force redirect to /maintenance
  if (isMaintenanceMode && !isOnMaintenancePage) {
    return <Navigate to="/maintenance" replace />;
  }

  // Maintenance OFF → redirect away from /maintenance
  if (!isMaintenanceMode && isOnMaintenancePage) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default MaintenanceGuard;
